import React, { useState, memo } from 'react';
import { useAuth, useLanguage, api } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Loader2, Leaf, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import DietaryBriefingModal from '../DietaryBriefingModal';

const DIETARY_APPROACHES = [
  { id: 'standard', labelFr: 'Standard (Flexible)', labelEn: 'Standard (Flexible)', descFr: 'Aucune restriction specifique', descEn: 'No specific restriction', color: 'border-blue-500 bg-blue-500/10', text: 'text-blue-400' },
  { id: 'keto', labelFr: 'Cetogene (Keto)', labelEn: 'Ketogenic (Keto)', descFr: 'Tres faible en glucides, riche en lipides', descEn: 'Very low carb, high fat', color: 'border-purple-500 bg-purple-500/10', text: 'text-purple-400' },
  { id: 'paleo', labelFr: 'Paleo', labelEn: 'Paleo', descFr: 'Aliments non-transformes uniquement', descEn: 'Unprocessed foods only', color: 'border-amber-500 bg-amber-500/10', text: 'text-amber-400' },
  { id: 'carnivore', labelFr: 'Carnivore', labelEn: 'Carnivore', descFr: 'Regime 100% produits animaux', descEn: '100% animal products', color: 'border-red-500 bg-red-500/10', text: 'text-red-400' },
  { id: 'vegetarian', labelFr: 'Vegetarien', labelEn: 'Vegetarian', descFr: 'Sans viande ni poisson', descEn: 'No meat or fish', color: 'border-green-500 bg-green-500/10', text: 'text-green-400' },
  { id: 'vegan', labelFr: 'Vegetalien (Vegan)', labelEn: 'Vegan', descFr: '100% vegetal, aucun produit animal', descEn: '100% plant-based', color: 'border-emerald-500 bg-emerald-500/10', text: 'text-emerald-400' },
  { id: 'pescatarian', labelFr: 'Pescetarien', labelEn: 'Pescatarian', descFr: 'Sans viande, avec poisson', descEn: 'No meat, with fish', color: 'border-cyan-500 bg-cyan-500/10', text: 'text-cyan-400' },
  { id: 'high_protein', labelFr: 'Haute Proteine', labelEn: 'High Protein', descFr: 'Priorite absolue aux proteines (2g+/kg)', descEn: 'Absolute protein priority (2g+/kg)', color: 'border-orange-500 bg-orange-500/10', text: 'text-orange-400' },
];

const SettingsDietaryApproach = memo(function SettingsDietaryApproach() {
  const { user, updateUser } = useAuth();
  const { language } = useLanguage();
  const [saving, setSaving] = useState(null);
  const [briefingId, setBriefingId] = useState(null);
  const fr = language === 'fr';

  const currentApproach = user?.dietary_approach || 'standard';

  const handleClick = (approachId) => {
    if (approachId === currentApproach) return;
    setBriefingId(approachId);
  };

  const handleConfirm = async () => {
    const approachId = briefingId;
    setBriefingId(null);
    setSaving(approachId);
    try {
      const res = await api.put('/users/profile', {
        dietary_approach: approachId,
        dietary_preferences: [approachId],
      });
      updateUser(res.data);
      const label = DIETARY_APPROACHES.find(a => a.id === approachId);
      toast.success(fr
        ? `Approche "${label?.labelFr}" activee ! L'IA est recalibree.`
        : `"${label?.labelEn}" approach activated! AI is recalibrated.`);
    } catch {
      toast.error(fr ? 'Erreur de sauvegarde' : 'Save error');
    } finally { setSaving(null); }
  };

  return (
    <>
      <Card className="bg-card border-border" data-testid="settings-dietary-approach">
        <CardHeader>
          <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
            <Leaf className="w-5 h-5 text-emerald-400" />
            {fr ? 'Mon Profil Nutritionnel' : 'My Nutritional Profile'}
          </CardTitle>
          <CardDescription>
            {fr
              ? 'Ton approche alimentaire calibre l\'IA. Toutes les recommandations, recettes et analyses s\'adaptent instantanement.'
              : 'Your dietary approach calibrates the AI. All recommendations, recipes, and analyses adapt instantly.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            {DIETARY_APPROACHES.map((approach) => {
              const isActive = currentApproach === approach.id;
              const isSaving = saving === approach.id;
              return (
                <button
                  key={approach.id}
                  type="button"
                  onClick={() => handleClick(approach.id)}
                  disabled={isSaving}
                  data-testid={`dietary-approach-${approach.id}`}
                  className={`relative p-3.5 rounded-xl text-center transition-all duration-200 border-2 ${
                    isActive
                      ? `${approach.color} shadow-lg`
                      : 'border-transparent bg-secondary/50 hover:border-muted-foreground/30'
                  }`}
                >
                  {isActive && (
                    <div className="absolute top-2 right-2">
                      <CheckCircle className={`w-4 h-4 ${approach.text}`} />
                    </div>
                  )}
                  {isSaving && (
                    <div className="absolute top-2 right-2">
                      <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    </div>
                  )}
                  <p className={`font-heading text-xs uppercase tracking-tight ${isActive ? approach.text : 'text-foreground'}`}>
                    {fr ? approach.labelFr : approach.labelEn}
                  </p>
                  <p className="text-[9px] text-muted-foreground mt-1 leading-snug">
                    {fr ? approach.descFr : approach.descEn}
                  </p>
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-muted-foreground/60 text-center mt-3 italic">
            {fr
              ? 'Changer d\'approche recalibre instantanement le Coach YUMIX.'
              : 'Changing approach instantly recalibrates the Coach YUMIX.'}
          </p>
        </CardContent>
      </Card>

      {/* Briefing Modal */}
      {briefingId && (
        <DietaryBriefingModal
          approachId={briefingId}
          language={language}
          onConfirm={handleConfirm}
          onCancel={() => setBriefingId(null)}
        />
      )}
    </>
  );
});

export default SettingsDietaryApproach;
